/* Penrose consent utility — wires the theme to Shopify's Customer Privacy API.
 *
 * Third-party scripts that need consent are deferred with:
 *   <script type="text/plain" data-consent-category="marketing" data-consent-src="https://..."></script>
 * and are activated once the visitor's consent allows that category
 * (including regions where consent isn't required).
 *
 * The consent banner itself is Shopify's built-in cookie banner
 * (Settings > Customer privacy > Cookie banner); this file only gates
 * scripts and wires the footer "Cookie preferences" link to that banner's
 * preferences dialog via window.privacyBanner.showPreferences().
 */
window.PenroseConsent = (function () {
  var api = null;
  var readyQueue = [];

  function ready(cb) {
    if (api) cb(api);
    else readyQueue.push(cb);
  }

  function allowed(category) {
    if (!api) return false;
    switch (category) {
      case 'analytics':
        return api.analyticsProcessingAllowed();
      case 'marketing':
        return api.marketingAllowed();
      case 'preferences':
        return api.preferencesProcessingAllowed();
      default:
        return false;
    }
  }

  function hasDecision() {
    if (!api) return false;
    var current = api.currentVisitorConsent();
    return current.marketing !== '' || current.analytics !== '' || current.preferences !== '';
  }

  function onConsent(category, cb) {
    ready(function () {
      if (allowed(category)) return cb();
      document.addEventListener('visitorConsentCollected', function handler() {
        if (allowed(category)) {
          document.removeEventListener('visitorConsentCollected', handler);
          cb();
        }
      });
    });
  }

  function setConsent(prefs, cb) {
    ready(function () {
      api.setTrackingConsent(prefs, cb || function () {});
    });
  }

  function activateScript(placeholder) {
    if (!placeholder || placeholder.dataset.consentActivated) return;
    placeholder.dataset.consentActivated = 'true';
    var script = document.createElement('script');
    if (placeholder.dataset.consentSrc) script.src = placeholder.dataset.consentSrc;
    else script.textContent = placeholder.textContent;
    placeholder.replaceWith(script);
  }

  function activateDeferredScripts() {
    document.querySelectorAll('script[type="text/plain"][data-consent-category]').forEach(function (el) {
      onConsent(el.dataset.consentCategory, function () {
        activateScript(el);
      });
    });
  }

  /* Stockist page: the map + shop list call Mapbox and Google Sheets, so they
   * stay unloaded until functional consent exists — with a click-to-load
   * button as a one-off escape hatch that doesn't change the consent record.
   * Registered before activateDeferredScripts so the map container is visible
   * (has dimensions) before Leaflet initializes. */
  function initStockistMap() {
    var map = document.getElementById('penrose-map');
    if (!map) return;
    var placeholder = document.getElementById('map-consent-placeholder');
    var loadButton = document.querySelector('[data-load-map]');
    var mapScript = document.getElementById('penrose-map-script');

    function reveal() {
      map.hidden = false;
      if (placeholder) placeholder.hidden = true;
    }

    onConsent('preferences', reveal);

    ready(function () {
      if (!allowed('preferences') && placeholder) placeholder.hidden = false;
    });

    if (loadButton) {
      loadButton.addEventListener('click', function () {
        reveal();
        activateScript(mapScript);
      });
    }
  }

  /* [data-consent-open] elements (footer "Cookie preferences" link) reopen the
   * banner with the preferences panel expanded, so consent can be withdrawn as
   * easily as it was given. Only revealed where the banner is relevant. */
  /* The footer "Cookie preferences" link reopens Shopify's own cookie banner
   * preferences dialog (Settings > Customer privacy > Cookie banner). Shopify
   * loads window.privacyBanner on every storefront page; the link is only
   * revealed when that API is present and the visitor is in a region where
   * the banner applies. */
  function nativeBanner() {
    var pb = window.privacyBanner;
    return pb && typeof pb.showPreferences === 'function' ? pb : null;
  }

  function initReopenLinks() {
    ready(function () {
      if (!nativeBanner()) return;
      if (api.shouldShowBanner() || hasDecision()) {
        document.querySelectorAll('[data-consent-open]').forEach(function (el) {
          el.hidden = false;
        });
      }
    });
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-consent-open]');
      if (!trigger) return;
      e.preventDefault();
      var pb = nativeBanner();
      if (pb) pb.showPreferences();
    });
  }

  /* Campaign Monitor forms post to an error endpoint unless the createsend
   * helper script (marketing-gated) has loaded, so intercept the submit and
   * ask for marketing consent instead of letting a broken POST through. */
  function initNewsletterGuard() {
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target.closest && e.target.closest('.js-cm-form');
        if (form && api && !allowed('marketing')) {
          e.preventDefault();
          var pb = nativeBanner();
          if (pb) pb.showPreferences();
        }
      },
      true
    );
  }

  function init() {
    try {
      localStorage.removeItem('penrose_cookie_ack');
    } catch (e) {}
    initNewsletterGuard();
    if (!window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
      if (error) return;
      api = window.Shopify.customerPrivacy;
      readyQueue.splice(0).forEach(function (cb) {
        cb(api);
      });
      initStockistMap();
      activateDeferredScripts();
      initReopenLinks();
      document.dispatchEvent(new CustomEvent('consent:ready'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    ready: ready,
    allowed: allowed,
    hasDecision: hasDecision,
    onConsent: onConsent,
    setConsent: setConsent,
    activateScript: activateScript
  };
})();
